import React from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import moment from 'moment';

import { serviceStopStatuses, extendedServiceStopStatuses } from 'types/timetableServices';
import { serviceStatuses } from 'types/timetableServices';

import { getTimetableStatusSummary } from 'utils/timetableServices';
import { TimetableStatusSpan } from './TimetableStatus.style';

const timetableStatusLabels = {
  [extendedServiceStopStatuses.ON_TIME]: 'journeySegment.timetableOnTime',
  [extendedServiceStopStatuses.CANCELLED]: 'journeySegment.timetableCancelled',
  [extendedServiceStopStatuses.EXPECTED]: 'journeySegment.timetableExpectedAt',
  [extendedServiceStopStatuses.ARRIVED]: 'journeySegment.timetableArrived',
  [extendedServiceStopStatuses.ARRIVED_ON_TIME]: 'journeySegment.timetableArrivedOnTime',
  [extendedServiceStopStatuses.DEPARTED]: 'journeySegment.timetableDeparted',
  [extendedServiceStopStatuses.DEPARTED_ON_TIME]: 'journeySegment.timetableDepartedOnTime',
  [extendedServiceStopStatuses.ARRIVED_LATE]: 'journeySegment.timetableArrivedAt',
  [extendedServiceStopStatuses.ARRIVED_EXPECTED_LATE]: 'journeySegment.timetableArrivedExpectedAt',
  [extendedServiceStopStatuses.ARRIVED_EARLY]: 'journeySegment.timetableArrivedAt',
  [extendedServiceStopStatuses.ARRIVED_EXPECTED_EARLY]: 'journeySegment.timetableArrivedExpectedAt',
  [extendedServiceStopStatuses.DEPARTED_LATE]: 'journeySegment.timetableDepartedAt',
  [extendedServiceStopStatuses.DEPARTED_EXPECTED_LATE]: 'journeySegment.timetableDepartedExpectedAt',
  [extendedServiceStopStatuses.DEPARTED_EARLY]: 'journeySegment.timetableDepartedAt',
  [extendedServiceStopStatuses.DEPARTED_EXPECTED_EARLY]: 'journeySegment.timetableDepartedExpectedAt'
};

const TimetableStopStatus = ({
  serviceStop,
  isBoardingNotPossible = false,
  isBoardingOnly = false,
  departure = false,
  arrival = false,
  serviceStatus
}) => {
  if (!serviceStop) {
    return null;
  }

  const { extendedArrivalStatus, arrivalDateTime, extendedDepartureStatus, departureDateTime } = getTimetableStatusSummary({ serviceStop });
  const departureTime = departureDateTime ? moment(departureDateTime).format('HH:mm') : null;
  const arrivalTime = arrivalDateTime ? moment(arrivalDateTime).format('HH:mm') : null;
  const departureStatus = serviceStatus === extendedServiceStopStatuses.CANCELLED ? serviceStatus : extendedDepartureStatus;
  const arrivalStatus = serviceStatus === extendedServiceStopStatuses.CANCELLED ? serviceStatus : extendedArrivalStatus;
  const platform = serviceStop?.model?.platformNumber;

  return (
    <>
      {departure && departureStatus && (
        <TimetableStatusSpan statusLevel={departureStatus}>
          <FormattedMessage
            id={isBoardingNotPossible ? 'journeySegment.timetableDepartureCancelled' : timetableStatusLabels[departureStatus]}
            values={{ time: departureTime }}
          />
          {platform && ['ON_TIME', 'EXPECTED'].includes(departureStatus) && (
            <div>
              <FormattedMessage id={'journeySegment.timetable.platform'} values={{ platform }} />
            </div>
          )}
        </TimetableStatusSpan>
      )}
      {arrival && arrivalStatus && (
        <TimetableStatusSpan statusLevel={arrivalStatus}>
          <FormattedMessage
            id={isBoardingOnly ? 'journeySegment.timetableArrivalCancelled' : timetableStatusLabels[arrivalStatus]}
            values={{ time: arrivalTime }}
          />
        </TimetableStatusSpan>
      )}
    </>
  );
};

TimetableStopStatus.propTypes = {
  serviceStop: PropTypes.shape({
    arrivalStatus: PropTypes.oneOf(Object.values(serviceStopStatuses)),
    departureStatus: PropTypes.oneOf(Object.values(serviceStopStatuses)),
    actualArrivalDateTime: PropTypes.string,
    actualDepartureDateTime: PropTypes.string,
    commercialArrivalDateTime: PropTypes.string,
    commercialDepartureDateTime: PropTypes.string,
    expectedArrivalDateTime: PropTypes.string,
    expectedDepartureDateTime: PropTypes.string,
    model: PropTypes.shape({ platformNumber: PropTypes.string })
  }),
  // Display the departure status for this serviceStop
  departure: PropTypes.bool,
  // Display the arrival status for this serviceStop
  arrival: PropTypes.bool,
  isBoardingNotPossible: PropTypes.bool,
  isBoardingOnly: PropTypes.bool,
  serviceStatus: PropTypes.oneOf(Object.keys(serviceStatuses))
};

export default injectIntl(TimetableStopStatus);
